import client, { upload } from './client';

/** Strips empty params so the query string stays clean. */
const q = (params = {}) => ({
  params: Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined),
  ),
});

export const authApi = {
  config: () => client.get('/auth/config'),
  requestOtp: mobile => client.post('/auth/otp/request', { mobile }),
  verifyOtp: (mobile, otp) => client.post('/auth/otp/verify', { mobile, otp }),
  login: (email, password) => client.post('/auth/login', { email, password }),
  me: () => client.get('/auth/me'),
  logout: () => client.post('/auth/logout', {}),
};

export const dashboardApi = {
  overview: () => client.get('/dashboard'),
};

export const userApi = {
  list: params => client.get('/users', q(params)),
  stats: () => client.get('/users/stats'),
  get: id => client.get(`/users/${id}`),
  create: body => client.post('/users', body),
  update: (id, body) => client.put(`/users/${id}`, body),
  block: (id, body) => client.patch(`/users/${id}/block`, body),
  remove: id => client.delete(`/users/${id}`),
};

export const companyApi = {
  list: params => client.get('/companies', q(params)),
  get: id => client.get(`/companies/${id}`),
  verify: (id, body) => client.patch(`/companies/${id}/verify`, body),
  reviewChangeRequest: (id, body) => client.patch(`/companies/${id}/change-request`, body),
  toggle: (id, isActive) => client.patch(`/companies/${id}/status`, { isActive }),
  setCredit: (id, creditLimit) => client.patch(`/companies/${id}/credit`, { creditLimit }),
  remove: id => client.delete(`/companies/${id}`),
};

export const categoryApi = {
  list: params => client.get('/categories', q(params)),
  tree: () => client.get('/categories/tree'),
  create: body => client.post('/categories', body),
  update: (id, body) => client.put(`/categories/${id}`, body),
  remove: id => client.delete(`/categories/${id}`),
  uploadImage: (id, formData) => upload(`/categories/${id}/image`, formData),
};

export const brandApi = {
  list: params => client.get('/brands', q(params)),
  get: id => client.get(`/brands/${id}`),
  create: body => client.post('/brands', body),
  update: (id, body) => client.put(`/brands/${id}`, body),
  remove: id => client.delete(`/brands/${id}`),
};

export const productApi = {
  list: params => client.get('/products', q(params)),
  get: id => client.get(`/products/${id}`),
  create: body => client.post('/products', body),
  update: (id, body) => client.put(`/products/${id}`, body),
  toggle: (id, isActive) => client.patch(`/products/${id}/status`, { isActive }),
  pricing: (id, body) => client.patch(`/products/${id}/pricing`, body),
  images: (id, formData) => upload(`/products/${id}/images`, formData),
  remove: id => client.delete(`/products/${id}`),
};

export const authorizationApi = {
  list: params => client.get('/authorizations', q(params)),
  stats: () => client.get('/authorizations/stats'),
  get: id => client.get(`/authorizations/${id}`),
  review: (id, body) => client.patch(`/authorizations/${id}/review`, body),
  revoke: (id, remarks) => client.patch(`/authorizations/${id}/revoke`, { remarks }),
};

export const warehouseApi = {
  list: params => client.get('/warehouses', q(params)),
  get: id => client.get(`/warehouses/${id}`),
  create: body => client.post('/warehouses', body),
  update: (id, body) => client.put(`/warehouses/${id}`, body),
  remove: id => client.delete(`/warehouses/${id}`),
};

export const inventoryApi = {
  list: params => client.get('/inventory', q(params)),
  summary: params => client.get('/inventory/summary', q(params)),
  movements: params => client.get('/inventory/movements', q(params)),
  stockIn: body => client.post('/inventory/stock-in', body),
  adjust: body => client.post('/inventory/adjust', body),
  update: (id, body) => client.put(`/inventory/${id}`, body),
};

export const orderApi = {
  list: params => client.get('/orders', q(params)),
  stats: () => client.get('/orders/stats'),
  get: id => client.get(`/orders/${id}`),
  track: id => client.get(`/orders/${id}/track`),
  setStatus: (id, body) => client.patch(`/orders/${id}/status`, body),
};

export const contractApi = {
  list: params => client.get('/contracts', q(params)),
  stats: () => client.get('/contracts/stats'),
  get: id => client.get(`/contracts/${id}`),
  terminate: (id, reason) => client.patch(`/contracts/${id}/terminate`, { reason }),
  runLifecycle: () => client.post('/contracts/run-lifecycle', {}),
};

export const paymentApi = {
  list: params => client.get('/payments', q(params)),
  summary: () => client.get('/payments/summary'),
  get: id => client.get(`/payments/${id}`),
  outstanding: params => client.get('/payments/outstanding', q(params)),
  refund: (id, body) => client.post(`/payments/${id}/refund`, body),
  releaseEscrow: id => client.patch(`/payments/${id}/release-escrow`, {}),
};

export const shipmentApi = {
  list: params => client.get('/shipments', q(params)),
  stats: () => client.get('/shipments/stats'),
  get: id => client.get(`/shipments/${id}`),
  assign: (id, body) => client.patch(`/shipments/${id}/assign`, body),
};

export const claimApi = {
  list: params => client.get('/claims', q(params)),
  stats: () => client.get('/claims/stats'),
  get: id => client.get(`/claims/${id}`),
  setStatus: (id, body) => client.patch(`/claims/${id}/status`, body),
  settle: (id, body) => client.post(`/claims/${id}/settle`, body),
};

export const insuranceApi = {
  providers: () => client.get('/insurance/providers'),
  policies: params => client.get('/insurance/policies', q(params)),
  policy: id => client.get(`/insurance/policies/${id}`),
  setPolicyStatus: (id, body) => client.patch(`/insurance/policies/${id}/status`, body),
  claims: params => client.get('/insurance/claims', q(params)),
  stats: () => client.get('/insurance/stats'),
};

export const financeApi = {
  partners: () => client.get('/finance/partners'),
  creditRequests: params => client.get('/finance/credit-requests', q(params)),
  creditRequest: id => client.get(`/finance/credit-requests/${id}`),
  review: (id, body) => client.patch(`/finance/credit-requests/${id}/review`, body),
  transactions: params => client.get('/finance/transactions', q(params)),
  escrow: params => client.get('/finance/escrow', q(params)),
  stats: () => client.get('/finance/stats'),
};

export const notificationApi = {
  list: params => client.get('/notifications', q(params)),
  unreadCount: () => client.get('/notifications/unread-count'),
  markRead: id => client.patch(`/notifications/${id}/read`, {}),
  markAllRead: () => client.patch('/notifications/read-all', {}),
  broadcast: body => client.post('/notifications/broadcast', body),
};

export const settingsApi = {
  get: () => client.get('/settings'),
  update: body => client.put('/settings', body),
  setOtpMode: body => client.patch('/settings/otp-mode', body),
  testPayment: () => client.post('/settings/test/payment', {}),
  testWhatsapp: mobile => client.post('/settings/test/whatsapp', { mobile }),
  testEmail: email => client.post('/settings/test/email', { email }),
  testDelhivery: () => client.post('/settings/test/delhivery', {}),
};

/** "Sold by" units offered to sellers. */
export const unitApi = {
  list: params => client.get('/units', q({ ...params, all: true })),
  create: body => client.post('/units', body),
  update: (id, body) => client.put(`/units/${id}`, body),
  remove: id => client.delete(`/units/${id}`),
};

/** Seller membership tiers and their listing caps. */
export const membershipApi = {
  list: params => client.get('/memberships', q({ ...params, all: true })),
  create: body => client.post('/memberships', body),
  update: (id, body) => client.put(`/memberships/${id}`, body),
  remove: id => client.delete(`/memberships/${id}`),
  assign: (companyId, body) => client.patch(`/memberships/companies/${companyId}`, body),
};

/** Fabric list offered to sellers. */
/** Home-screen picks and listing review live on the product itself. */
export const productExtrasApi = {
  setFeatured: (id, isFeatured) => client.patch(`/products/${id}/featured`, { isFeatured }),
};

export const materialApi = {
  list: params => client.get('/materials', q({ ...params, all: true })),
  create: body => client.post('/materials', body),
  update: (id, body) => client.put(`/materials/${id}`, body),
  remove: id => client.delete(`/materials/${id}`),
};

/** Listings awaiting review before buyers can see them. */
export const approvalApi = {
  list: params => client.get('/products/pending', q(params)),
  review: (id, body) => client.patch(`/products/${id}/approval`, body),
};

/** Cancellation / return / exchange reason lists. */
export const reasonApi = {
  list: params => client.get('/reasons', q({ ...params, all: true })),
  create: body => client.post('/reasons', body),
  update: (id, body) => client.put(`/reasons/${id}`, body),
  remove: id => client.delete(`/reasons/${id}`),
};

/** Buyer cancellation requests awaiting a decision. */
export const cancellationApi = {
  list: params => client.get('/orders/cancellations', q(params)),
  review: (orderId, body) => client.patch(`/orders/${orderId}/cancellation`, body),
};

/** Support desk. */
export const ticketApi = {
  list: params => client.get('/tickets', q(params)),
  stats: () => client.get('/tickets/stats'),
  get: id => client.get(`/tickets/${id}`),
  reply: (id, body) => client.post(`/tickets/${id}/reply`, { body }),
  resolve: (id, note) => client.patch(`/tickets/${id}/resolve`, { note }),
};

/** Returns and exchanges — admin can act on the seller's behalf. */
export const returnApi = {
  list: params => client.get('/returns', q(params)),
  get: id => client.get(`/returns/${id}`),
  updateStatus: (id, body) => client.patch(`/returns/${id}/status`, body),
  stats: () => client.get('/returns/stats'),
};

export const reportApi = {
  sales: params => client.get('/reports/sales', q(params)),
  purchase: params => client.get('/reports/purchase', q(params)),
  revenue: params => client.get('/reports/revenue', q(params)),
  inventory: params => client.get('/reports/inventory', q(params)),
  contracts: params => client.get('/reports/contracts', q(params)),
  distributors: params => client.get('/reports/distributors', q(params)),
  orders: params => client.get('/reports/orders', q(params)),
  gst: params => client.get('/reports/gst', q(params)),
  companies: params => client.get('/reports/companies', q(params)),
};
